import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { ChartsWrapperComponent } from '../../charts-wrapper/charts-wrapper.component';
import { ChartDatasets } from '../../charts-wrapper/charts.model';
import { LoaderComponent } from '../../../../../ui-components/lib/loader/loader.component';
import { ReportService } from '../../../services/report/report.service';
import {
  InsightChartRow,
  InsightChartRowView,
  toInsightRowViews,
} from '../insight-chart.model';

/**
 * Donut with a legend of count + share per row. When `selectable`, a legend row emits its
 * row (or null when the active row is picked again), so the page can filter by it.
 */
@Component({
  selector: 'lib-donut-legend-chart',
  imports: [ChartsWrapperComponent, LoaderComponent],
  templateUrl: './donut-legend-chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class DonutLegendChartComponent {
  private readonly reportService = inject(ReportService);

  /** Unique canvas id for the donut. */
  readonly chartId = input.required<string>();
  readonly rows = input<InsightChartRow[]>([]);
  /** Label above the total in the donut's centre. */
  readonly centerTitle = input<string>('');
  readonly loading = input<boolean>(false);
  /** Opt-in: legend rows become buttons that emit `rowSelect`; off, the legend is display-only. */
  readonly selectable = input<boolean>(false);
  /** Key of the row the page is filtered by; the other rows dim. */
  readonly activeKey = input<string | null>(null);

  readonly rowSelect = output<InsightChartRow | null>();

  protected readonly rowClass = computed(() =>
    this.selectable()
      ? 'cursor-pointer hover:bg-neutral-50 focus-visible:ring-2 focus-visible:ring-updated-primary-300'
      : 'cursor-default'
  );

  protected readonly rowViews = computed<InsightChartRowView[]>(() =>
    toInsightRowViews(this.rows())
  );

  protected readonly total = computed(() =>
    this.rows().reduce((sum, row) => sum + row.value, 0)
  );

  protected readonly datasets = computed<ChartDatasets[]>(() => [
    {
      label: this.centerTitle(),
      data: this.rows().map(row => row.value),
      backgroundColor: this.rows().map(row => row.color),
      hoverOffset: 4,
    },
  ]);

  protected readonly labels = computed(() => this.rows().map(row => row.label));

  protected readonly size = this.reportService.getCurrentValueInPixels(10);
  protected readonly cutout = this.reportService.getCurrentValueInPixels(3.75);

  select(row: InsightChartRowView): void {
    if (!this.selectable()) {
      return;
    }
    const { key, label, value, color } = row;
    this.rowSelect.emit(
      this.activeKey() === key ? null : { key, label, value, color }
    );
  }
}
